import React, { FunctionComponent as F, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { Checkbox, FilterOptionsState, FormControl, FormControlLabel, Grid } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import { removeSharedProtocolAsync, shareProtocolAsync, updateEditAsync, updatePublicAsync } from "../../core/store/protocol/protocol.thunks";
import { useAppDispatch } from "../../core/store/hooks";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import Button from '../UI/Button/Button';
import ControlledAutocomplete from "../UI/Input/ControlledAutocomplete";
import { IOption } from "@protoplan/types";
import { getMatchingUsers } from "../../core/http/user";
import TextTooltip from "../UI/Tooltip/TextTooltip";
import EditIcon from '@mui/icons-material/Edit';
import { alphabeticalSort } from "../../utils/string";
import ControlledCreatableAutocomplete from "../UI/Input/ControlledCreatableAutocomplete";
import { validateEmail } from "../../utils/validate";
import { useSelector } from "react-redux";
import { IState } from "../../core/store/store";



interface Props
{
  protocolId: number,
  handleClose: () => void,
  open: boolean
}

interface UserOption extends IOption
{
  userId: number,
  username: string,
  email: string,
}

const ShareProtocolDialog: F<Props> = (props: Props) =>
{
  const appDispatch = useAppDispatch();
  const protocol = useSelector((state: IState) => state.protocol.protocols
    .find(p => p.protocolId === props.protocolId));

  const [options, setOptions] = useState(Array<UserOption>());
  const [currentOption, setCurrentOption] = useState<UserOption | null>(null);
  const [message, setMessage] = useState<string | undefined>();

  if(!protocol)
    return <></>;

  const userOptionsFilter = (opts: IOption[]) => opts.filter((o) =>
    !protocol.sharedWith.filter(u => u.userId === o.value).length &&
    o.value !== protocol.userId);


  const filterUserOptions = (opts: IOption[], params: FilterOptionsState<IOption>) =>
  {
    const filtered = userOptionsFilter(opts);
    const { inputValue } = params;

    // Suggest the creation of a new value
    const isExisting = opts.some(option => inputValue === option.label);
    if(inputValue !== '' && !isExisting && validateEmail(inputValue))
      filtered.push({ input: inputValue, label: `Add "${inputValue}"`, value: 0 });

    return filtered;
  };

  const handleInputChange = async(_event: React.SyntheticEvent, value: string) =>
  {
    if(!value)
      return setOptions([]);

    // Partial email or username match
    const users = (await getMatchingUsers(value))?.users;
    if(!users)
      return setOptions([]);

    const options = users.map(u => ({ ...u, value: u.userId, label: u.username }));

    setOptions(options);
  }

  const handleChangeCurrentOption = async(option: UserOption | null) =>
  {
    setCurrentOption(option);
  }

  const handleInviteClick = async() =>
  {
    if(!currentOption)
      return;

    const result = await appDispatch(shareProtocolAsync(
      props.protocolId,
      currentOption.userId,
      (!currentOption.value && currentOption.input && validateEmail(currentOption.input)) ?
        currentOption.label :
        undefined));

    if(result?.success === false)
    {
      return setMessage("Failed to send invite email");
    }
    else if(result?.username)
    {
      setMessage("Successfully shared with " + result.username + ". ");
    }
    else if(result?.success)
    {
      setMessage(
        "Invite email sent to " + currentOption.label + ". " +
        "Leave public viewing enabled so they can view. ");
    }

    setCurrentOption(null);
  }

  const handleEditClick = async(userId: number) =>
  {
    await appDispatch(updateEditAsync(
      props.protocolId,
      userId,
      !protocol.sharedWith.find(u => u.userId === userId)?.edit));
  }

  const handleDelete = async(userId: number) =>
  {
    await appDispatch(removeSharedProtocolAsync(props.protocolId, userId));
  }

  const sharedWith = [...protocol.sharedWith].sort((a, b) => alphabeticalSort(a.username, b.username));

  return (
    <Dialog
      maxWidth='md'
      PaperProps={{ sx: { p: '15px', textAlign: 'center', display: 'flex', alignItems: 'center', maxWidth: 600 }}}
      open={props.open}
      onClose={props.handleClose}>
      <DialogTitle sx={{ fontSize: '30px' }}>{"Share " + protocol.name}</DialogTitle>
      <DialogContent sx={{ maxWidth: 500 }}>

        <Stack spacing={2}>

          {sharedWith.map((u, i) =>
          {
            return(
              <Grid container direction='row' key={i}>
                <Grid item xs key={0} style={{ display: "flex", justifyContent: "flex-start" }}>
                  <Typography variant='body2'>
                    {u.username}
                  </Typography>
                </Grid>
                <Grid item xs key={1} style={{ display: "flex", justifyContent: "flex-end" }}>
                  <TextTooltip lines={[u.edit ?
                    u.username + " can edit - click to disallow" :
                    ("Click to allow " + u.username + " to edit")]} tooltipPlacement='right-end'>
                    <EditIcon
                      fontSize='small'
                      sx={{ color: u.edit ? 'teal' : 'gray', cursor: 'pointer', paddingInline: 0.5 }}
                      onClick={() => handleEditClick(u.userId)}/>
                  </TextTooltip>
                  <TextTooltip lines={["Stop sharing with " + u.username]} tooltipPlacement='right-end'>
                    <DeleteIcon
                      fontSize='small'
                      sx={{ color: 'gray', cursor: 'pointer', paddingInline: 0.5 }}
                      onClick={() => handleDelete(u.userId)}/>
                  </TextTooltip>
                </Grid>
              </Grid>)
          })}

          <Box style={{ display: 'inline', paddingLeft: '4px' }} />

          <Grid container direction='row' key={protocol.sharedWith.length}>
            <Grid item xs key={0} style={{ display: "flex", justifyContent: "flex-start" }}>
              {protocol.public ?
                // If a protocol is public, allow sharing to an unregistered email address
                <ControlledCreatableAutocomplete
                  label='Invite'
                  width='250px'
                  onInputChange={handleInputChange}
                  handleChangeCurrentOption={handleChangeCurrentOption as (option: IOption | null) => Promise<void>}
                  options={options}
                  currentOption={currentOption}
                  // For filtering and/or adding options, replaces filter
                  filterOptions={filterUserOptions} /> :
                <ControlledAutocomplete
                  label='Invite'
                  width='250px'
                  onInputChange={handleInputChange}
                  onChangeCurrentOption={handleChangeCurrentOption as (option: IOption | null) => Promise<void>}
                  options={options}
                  currentOption={currentOption}
                  filter={userOptionsFilter} />}
            </Grid>
            <Grid item xs key={1} style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button label='Invite' onClick={handleInviteClick} disabled={!currentOption}/>
            </Grid>
          </Grid>

          <Box style={{ width: 300 }} >
            <Typography variant='body2'>
              {message}
            </Typography>
          </Box>

          <FormControl component='fieldset'>
            <FormControlLabel
              label={'Enable public viewing at this link'}
              control={<Checkbox
                checked={protocol.public}
                onChange={() => appDispatch(updatePublicAsync(protocol.protocolId, !protocol.public))}
                inputProps={{ 'aria-label': 'controlled' }}/>}/>
          </FormControl>

          <Box style={{ display: 'inline', paddingLeft: '4px' }} />

          <Grid container direction='row' key={protocol.sharedWith.length + 1}>
            <Grid item xs style={{ display: "flex", justifyContent: "center" }}>
              <Button
                label='Ok'
                onClick={props.handleClose}/>
            </Grid>
          </Grid>

        </Stack>
      </DialogContent>
    </Dialog>);
}

export default ShareProtocolDialog;

